<template>
  <div v-if="isPasswordProtected" class="flex h-dvh items-center justify-center bg-background">
    <div class="container">
      <div class="mx-auto max-w-96">
        <PasswordProtectForm />
      </div>
    </div>
  </div>
  <div v-else :class="backgroundClass" class="relative min-h-dvh">
    <Header
      v-if="!route.meta.noHeader && isAuthenticated"
      :has-back-button="route.name === 'FanzonePost'"
    />
    <router-view v-slot="{ Component }">
      <transition :name="transitionName" mode="out-in">
        <component :is="Component" :key="$route.path" class="z-10" />
      </transition>
    </router-view>
    <Footer v-if="!route.meta.noFooter" />
    <PhotoWrapper v-if="!route.meta.noPhoto" :class="photoClasses" />
    <div
      class="pointer-events-none fixed top-0 -z-10 h-screen w-screen"
      :class="backgroundClass"
    ></div>
    <NotForYou
      v-if="!route.meta.preview && isAuthenticated && emailMismatch"
      :isOpen="emailMismatch"
    />
    <MissingDetails
      v-else-if="!route.meta.preview && isAuthenticated && hasMissingFields"
      :isOpen="hasMissingFields"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch } from 'vue';
import { useCookies } from '@vueuse/integrations/useCookies';
import { useFanStore } from '@stores/fan.store';
import { useRoute } from 'vue-router';
import { useAccountStore } from '@stores/account.store';
import { storeToRefs } from 'pinia';

import Header from '@generics/Header.vue';
import Footer from '@generics/Footer.vue';
import PhotoWrapper from '@modules/PhotoWrapper.vue';
import PasswordProtectForm from '@modules/PasswordProtectForm.vue';
import MissingDetails from '@modules/MissingDetails/MissingDetails.vue';
import NotForYou from '@modules/NotForYou/NotForYou.vue';

const route = useRoute();

const cookies = useCookies(['stored-password']);
const fanStore = useFanStore();
const { hasMissingFields } = storeToRefs(fanStore);
const accountStore = useAccountStore();
const { isAuthenticated } = storeToRefs(accountStore);

// NOTE: this is called once on created
accountStore.readAuthToken();

const emailMismatch = computed(
  () =>
    accountStore.guestEmail && fanStore.fanEmail && accountStore.guestEmail !== fanStore.fanEmail,
);

const previousRoute = ref(route);

const isPasswordProtected = computed(
  () =>
    import.meta.env.VITE_FF_PASSWORD &&
    cookies.get('stored-password') !== import.meta.env.VITE_FF_PASSWORD,
);

const backgroundClass = computed(() => {
  const backgroundVariants = {
    black: 'bg-black flex flex-col min-h-dvh',
    paper: `bg-[url('../paper_texture.webp')] bg-repeat flex flex-col`,
    default: 'bg-background',
  };

  switch (route.name) {
    case 'Home':
      return backgroundVariants.black;
    case 'FanzoneIndex':
    case 'FanzonePost':
      return backgroundVariants.paper;
    default:
      return backgroundVariants.default;
  }
});

const photoClasses = computed(() => {
  const sizes = {
    Default: 'h-[60%]',
    Profile: 'hidden lg:flex',
  };

  switch (route.name) {
    case 'Profile':
      return sizes.Profile;
    default:
      return sizes.Default;
  }
});

watch(
  () => route.fullPath,
  () => {
    previousRoute.value = route;
  },
  { immediate: true },
);

const transitionName = computed(() => {
  const from = previousRoute.value.path;
  const to = route.path;

  return from === '/' || to === '/' ? undefined : 'slide';
});

onMounted(async () => {
  if (isPasswordProtected.value && cookies.get('stored-password')) {
    cookies.remove('stored-password');
  }
});
</script>

<style>
/* Slide */
.slide-enter-active,
.slide-leave-active {
  transition:
    opacity 0.25s,
    transform 0.25s;
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(1rem);
}

/* Fade */
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.25s,
    transform 0.25s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(0.25rem);
}
</style>
